import React from "react";
import { clsx } from "clsx";

import {
    type MediaConfig,
    Media,
    MediaFormat,
    sourceHasValidMedia,
} from "~components/shared/media/";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import {
    type CuratedItem,
    type VariationComponent,
    getIsOpinionPageTheme,
    ZoneColumnCombo,
} from "~types/types";
import isBottomRowOf from "~utils/isBottomRowOf";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContent3Up.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContent3Up.module.css";

const PLACEMENT_TO_CLASS = ["start", "middleOfThree", "end"];

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];
export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.snapshotChart,
    MediaFormat.embeddedIframe,
    MediaFormat.loopingVideo,
    MediaFormat.videoImage,
    MediaFormat.defaultImage,
];
const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.media,
    desktopMaxWidth: 265,
    mobileMaxWidth: 330,
    showCaption: false,
};

const ThreeUpItem = (props: {
    item: CuratedItem;
    itemIndex: number;
    totalItems: number;
    theme?: string;
}) => {
    const { item, itemIndex, totalItems, theme } = props;

    if (!item) return null;

    // using classes to add/remove borders from the bottom row of the grid
    // two items per row on mobile vs. 3 items per row on tablet
    // plus adding borders in between rows makes this necessary.
    const threeUpRowPosition = itemIndex % 3;
    const placementClass = PLACEMENT_TO_CLASS[threeUpRowPosition];

    const itemClasses = clsx(
        styles.item,
        styles[placementClass],
        isBottomRowOf({ itemsPerRow: 2, itemIndex, totalItems }) &&
            styles.bottomRowOfTwo,
        isBottomRowOf({ itemsPerRow: 3, itemIndex, totalItems }) &&
            styles.bottomRowOfThree
    );

    return (
        <div className={itemClasses} key={item.url}>
            <Media
                source={item}
                priority={MEDIA_PRIORITY}
                config={MEDIA_CONFIG}
            />
            <div className={styles.storyContent}>
                <StoryBlock
                    storyIndex={itemIndex + 1}
                    story={item}
                    withDek={false}
                    isBylineEligible={false}
                    isSecondaryStory
                    isExtraSmallHeadline
                    isAuthorEyebrow={getIsOpinionPageTheme(theme)}
                />
            </div>
        </div>
    );
};

export default function LineupContent3Up(props: VariationComponent) {
    const integrity = integrityCheck(props);
    if (integrity.length) return integrityError(integrity);

    const { items = [], theme } = props;
    const itemsWithMedia = items.filter((item) =>
        sourceHasValidMedia(item, MEDIA_PRIORITY)
    );

    return (
        <VariantBaseWithHeader
            source={props}
            className={styles.LineupContent3Up}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            {itemsWithMedia.length > 0 && (
                <div className={styles.items}>
                    {itemsWithMedia.map((item, index) => (
                        <ThreeUpItem
                            itemIndex={index}
                            totalItems={itemsWithMedia.length}
                            item={item}
                            key={item.id}
                            theme={theme}
                        />
                    ))}
                </div>
            )}
        </VariantBaseWithHeader>
    );
}
